import { z } from "zod";

import type { DbEntity } from "../../../client";
import { AiTaskModelNoticeSchema } from "../../../schemas/ModelConfig/model-notice.schema";
import type { AITaskMetadataSnapshot } from "../../../shared/domain/tools/ai-task-version.types";
import type {
  AITaskWithFewShots,
  TaskCreationSchemaType,
  TaskFormSchemaType
} from "../../../shared/domain/tools/aitask.types";
import { ApiSuccessSchema } from "../../common/response.schema";
import { AITaskKindSchema } from "./schemas/ai-task-kind.schema";
import {
  AITaskVersionDetailSchema,
  AITaskVersionListResponseSchema,
  AITaskVersionMetadataSchema,
  AITaskVersionStatusSchema,
  AITaskVersionTypeSchema
} from "./schemas/ai-task-version.schema";

// ─── Shared response schemas for the AI-task CRUD + test surface ──────────

/** Flat task shape returned by Create (mirrors the backend taskResponse DTO). */
const TaskCreateResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  prompt: z.string().nullable(),
  category: z.string(),
  kind: AITaskKindSchema,
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  organizationId: z.string().nullable()
});

/** Prompt generation / regeneration response. */
const PromptGenerationResponseSchema = z.object({
  prompt: z.string(),
  metadata: z.object({
    category: z.string(),
    generatedAt: z.string()
  }),
  // config echoes back the submitted form data; shape is the task form, kept opaque here.
  config: z.record(z.string(), z.unknown())
});

/**
 * Result of an AI-task test run. The producers spread a dynamic service result
 * (output, tokensUsed, …) onto a base envelope, so unknown keys are preserved.
 */
const AITaskTestResultSchema = z
  .object({
    success: z.boolean().optional(),
    output: z.unknown().optional(),
    outputType: z.string().optional(),
    error: z.string().optional(),
    executionTime: z.number().optional(),
    modelNotice: AiTaskModelNoticeSchema.optional(),
    tokensUsed: z.object({ inputTokens: z.number(), outputTokens: z.number() }).optional(),
    documentInfo: z
      .object({
        fileName: z.string(),
        fileSize: z.number(),
        mimeType: z.string(),
        transcriptLength: z.number()
      })
      .optional()
  })
  .passthrough();

/** Immediate response of POST :taskId/test — returns a job id to poll. */
const AITaskTestJobSchema = z.object({
  jobId: z.string(),
  status: z.string()
});

/** Poll response of GET job/:jobId. */
const AITaskJobStatusSchema = z.object({
  jobId: z.string(),
  status: z.string(),
  data: z
    .object({
      success: z.boolean(),
      output: z.unknown().optional(),
      outputType: z.string().optional(),
      modelNotice: AiTaskModelNoticeSchema.optional(),
      error: z.string().optional()
    })
    .passthrough()
    .nullable(),
  error: z.string().nullable(),
  executionTime: z.number().nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable()
});

/** Result of a bulk task operation (delete). */
const BulkOperationResultSchema = z.object({
  success: z.array(z.string()),
  failed: z.array(z.object({ id: z.string(), error: z.string() })),
  totalRequested: z.number(),
  totalSuccess: z.number(),
  totalFailed: z.number()
});

/** Acknowledgement for delete routes (envelope needs a body, so no 204). */
const AITaskOkSchema = z.object({ success: z.literal(true) });

export const ZAiTasks = {
  Get: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/:id",
    PathVars: z.object({ id: z.string() }),
    // z.custom: shape guaranteed by backend; no structural validation
    Response: ApiSuccessSchema(z.custom<AITaskWithFewShots>())
  },

  // ── Task CRUD ───────────────────────────────────────────────────────

  /** GET /api/tools/ai-tasks — list all tasks for the org. */
  List: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks",
    Response: ApiSuccessSchema(z.array(z.custom<DbEntity.AITask>()))
  },

  /**
   * POST /api/tools/ai-tasks/create — create a task.
   * Body is the Prisma-coupled taskCreation form; typed via z.custom (validated
   * server-side against taskCreationSchema) to keep Prisma runtime out of the bundle.
   * `kind` rides beside the form (absent means GENERATIVE); it is absent from
   * Update because a task's kind never changes.
   */
  Create: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/create",
    Body: z.custom<TaskCreationSchemaType & { kind?: z.infer<typeof AITaskKindSchema> }>(),
    Response: ApiSuccessSchema(TaskCreateResponseSchema)
  },

  /** PUT /api/tools/ai-tasks/:taskId — update a task (full form). */
  Update: {
    method: "PUT" as const,
    path: "/api/tools/ai-tasks/:taskId",
    PathVars: z.object({ taskId: z.string() }),
    Body: z.custom<TaskCreationSchemaType>(),
    Response: ApiSuccessSchema(z.custom<AITaskWithFewShots>())
  },

  /** DELETE /api/tools/ai-tasks/:taskId — delete a task. */
  Delete: {
    method: "DELETE" as const,
    path: "/api/tools/ai-tasks/:taskId",
    PathVars: z.object({ taskId: z.string() }),
    Response: ApiSuccessSchema(AITaskOkSchema)
  },

  /** POST /api/tools/ai-tasks/:taskId/duplicate — duplicate a task (returns the new task). */
  Duplicate: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/duplicate",
    PathVars: z.object({ taskId: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.AITask>())
  },

  /** POST /api/tools/ai-tasks/:taskId/update-prompt — update just the prompt. */
  UpdatePrompt: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/update-prompt",
    PathVars: z.object({ taskId: z.string() }),
    Body: z.object({ prompt: z.string() }),
    Response: ApiSuccessSchema(z.custom<DbEntity.AITask>())
  },

  /** POST /api/tools/ai-tasks/bulk/delete — delete many tasks. */
  BulkDelete: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/bulk/delete",
    Body: z.object({ taskIds: z.array(z.string()) }),
    Response: ApiSuccessSchema(BulkOperationResultSchema)
  },

  // ── Prompt generation ───────────────────────────────────────────────

  /** POST /api/tools/ai-tasks/generate-prompt — generate a prompt from the form. */
  GeneratePrompt: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/generate-prompt",
    Body: z.custom<TaskFormSchemaType>(),
    Response: ApiSuccessSchema(PromptGenerationResponseSchema)
  },

  /** POST /api/tools/ai-tasks/:taskId/regenerate-prompt — regenerate for an existing task. */
  RegeneratePrompt: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/regenerate-prompt",
    PathVars: z.object({ taskId: z.string() }),
    Body: z.custom<TaskFormSchemaType & { includeFewShots?: boolean }>(),
    Response: ApiSuccessSchema(PromptGenerationResponseSchema)
  },

  // ── Test execution ──────────────────────────────────────────────────

  /** POST /api/tools/ai-tasks/:taskId/test — start an async test run, returns a job id. */
  Test: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/test",
    PathVars: z.object({ taskId: z.string() }),
    Body: z.object({
      input: z.union([z.string(), z.record(z.string(), z.unknown())])
    }),
    Response: ApiSuccessSchema(AITaskTestJobSchema)
  },

  /** GET /api/tools/ai-tasks/job/:jobId — poll a test job's status/result. */
  GetJobStatus: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/job/:jobId",
    PathVars: z.object({ jobId: z.string() }),
    Response: ApiSuccessSchema(AITaskJobStatusSchema)
  },

  /** POST /api/tools/ai-tasks/:taskId/test-with-document — test against an uploaded document (multipart). */
  TestWithDocument: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/test-with-document",
    PathVars: z.object({ taskId: z.string() }),
    Body: z.custom<FormData>(),
    Response: ApiSuccessSchema(AITaskTestResultSchema)
  },

  // ── Few-shot examples ───────────────────────────────────────────────

  /** POST /api/tools/ai-tasks/few-shots — create a few-shot example. */
  CreateFewShot: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/few-shots",
    Body: z.object({
      input: z.string(),
      output: z.string(),
      aiTaskId: z.string()
    }),
    Response: ApiSuccessSchema(z.custom<DbEntity.FewShot>())
  },

  /** PATCH /api/tools/ai-tasks/few-shots/:id — update a few-shot example. */
  UpdateFewShot: {
    method: "PATCH" as const,
    path: "/api/tools/ai-tasks/few-shots/:id",
    PathVars: z.object({ id: z.string() }),
    Body: z.object({
      input: z.string().optional(),
      output: z.string().optional()
    }),
    Response: ApiSuccessSchema(z.custom<DbEntity.FewShot>())
  },

  /** DELETE /api/tools/ai-tasks/few-shots/:id — delete a few-shot example. */
  DeleteFewShot: {
    method: "DELETE" as const,
    path: "/api/tools/ai-tasks/few-shots/:id",
    PathVars: z.object({ id: z.string() }),
    Response: ApiSuccessSchema(AITaskOkSchema)
  },

  // ── Versions (checkpoints & auto-saves) ─────────────────────────────
  // All routes nested under /api/tools/ai-tasks/:taskId/versions

  /** GET /api/tools/ai-tasks/:taskId/versions — paginated list (metadata only) */
  ListVersions: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/:taskId/versions",
    PathVars: z.object({ taskId: z.string() }),
    Query: z.object({
      limit: z.coerce.number().int().min(1).max(100).optional(),
      offset: z.coerce.number().int().min(0).optional(),
      type: AITaskVersionTypeSchema.optional()
    }),
    Response: ApiSuccessSchema(AITaskVersionListResponseSchema)
  },

  /** GET /api/tools/ai-tasks/:taskId/versions/status — lightweight status for editor header */
  GetVersionStatus: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/:taskId/versions/status",
    PathVars: z.object({ taskId: z.string() }),
    Response: ApiSuccessSchema(AITaskVersionStatusSchema)
  },

  /** GET /api/tools/ai-tasks/:taskId/versions/:versionId — full version with metadata snapshot */
  GetVersion: {
    method: "GET" as const,
    path: "/api/tools/ai-tasks/:taskId/versions/:versionId",
    PathVars: z.object({ taskId: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(AITaskVersionDetailSchema)
  },

  /** POST /api/tools/ai-tasks/:taskId/versions — create a manual checkpoint */
  CreateCheckpoint: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/versions",
    PathVars: z.object({ taskId: z.string() }),
    Body: z.object({
      // z.custom: snapshot built by the frontend's buildMetadataSnapshot; stored as-is
      metadata: z.custom<AITaskMetadataSnapshot>(),
      name: z.string().max(100).optional(),
      description: z.string().max(500).optional()
    }),
    Response: ApiSuccessSchema(AITaskVersionMetadataSchema)
  },

  /** POST /api/tools/ai-tasks/:taskId/versions/auto — auto-save (type=AUTO, minimal response) */
  CreateAutoSave: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/versions/auto",
    PathVars: z.object({ taskId: z.string() }),
    Body: z.object({
      metadata: z.custom<AITaskMetadataSnapshot>()
    }),
    Response: ApiSuccessSchema(z.object({ id: z.string(), createdAt: z.string() }))
  },

  /** PATCH /api/tools/ai-tasks/:taskId/versions/:versionId — update name/description */
  UpdateVersion: {
    method: "PATCH" as const,
    path: "/api/tools/ai-tasks/:taskId/versions/:versionId",
    PathVars: z.object({ taskId: z.string(), versionId: z.string() }),
    Body: z.object({
      name: z.string().max(100).optional(),
      description: z.string().max(500).optional()
    }),
    Response: ApiSuccessSchema(AITaskVersionMetadataSchema)
  },

  /** POST /api/tools/ai-tasks/:taskId/versions/:versionId/restore — restore snapshot to task */
  RestoreVersion: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/versions/:versionId/restore",
    PathVars: z.object({ taskId: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(
      z.object({ metadata: z.custom<AITaskMetadataSnapshot>(), message: z.string() })
    )
  },

  /** POST /api/tools/ai-tasks/:taskId/versions/:versionId/publish — set version as production */
  PublishVersion: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/versions/:versionId/publish",
    PathVars: z.object({ taskId: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(z.object({ versionId: z.string(), message: z.string() }))
  },

  /** POST /api/tools/ai-tasks/:taskId/versions/publish-draft — set current draft as production */
  PublishDraft: {
    method: "POST" as const,
    path: "/api/tools/ai-tasks/:taskId/versions/publish-draft",
    PathVars: z.object({ taskId: z.string() }),
    Response: ApiSuccessSchema(z.object({ message: z.string() }))
  },

  /** DELETE /api/tools/ai-tasks/:taskId/versions/:versionId — delete a checkpoint version */
  DeleteVersion: {
    method: "DELETE" as const,
    path: "/api/tools/ai-tasks/:taskId/versions/:versionId",
    PathVars: z.object({ taskId: z.string(), versionId: z.string() }),
    Response: ApiSuccessSchema(z.object({ id: z.string(), deleted: z.boolean() }))
  }
} as const;
